import { Alert, Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { FORMS_UNMATCHED_REPLIES } from "../../config/urls";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import AppTable from "../../common/app-table";

const ROWS_PER_PAGE = 50;

const REASON_LABELS = {
  UNKNOWN_TOKEN: "Unknown form token",
  EXPIRED_TOKEN: "Expired form token",
  BAD_BODY: "Reply couldn't be read",
};

/**
 * Admin-only: form replies that couldn't be matched to a send. Kept for 30 days,
 * and only the names of the answered fields are stored, never the values.
 */
const UnmatchedReplies = ({ onNavigate }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await tokenRef.current();
      const params = new URLSearchParams({
        limit: ROWS_PER_PAGE,
        offset: page * ROWS_PER_PAGE,
      });
      const response = await fetch(`${FORMS_UNMATCHED_REPLIES}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (response.status === 403) {
        throw new Error("Only admins can view unmatched replies.");
      }
      if (!response.ok) throw new Error("Failed to fetch unmatched replies");
      const data = await response.json();
      setRows(data.results);
      setCount(data.count);
    } catch (err) {
      setError(err.message || "Failed to fetch unmatched replies");
    } finally {
      setLoading(false);
    }
  }, [page, tenant]);

  useEffect(() => {
    load();
  }, [load]);

  const body = { ...typography.bodyLarge, color: "#1D2330" };

  const columns = [
    {
      id: "received_at",
      label: "Received",
      render: (row) => (
        <Typography sx={body}>{dayjs(row.received_at).format("D MMM, HH:mm")}</Typography>
      ),
    },
    {
      id: "phone_masked",
      label: "Phone",
      render: (row) => <Typography sx={body}>{row.phone_masked || "—"}</Typography>,
    },
    {
      id: "reason",
      label: "Reason",
      render: (row) => (
        <Typography sx={body}>{REASON_LABELS[row.reason] || row.reason}</Typography>
      ),
    },
    {
      id: "answer_keys",
      label: "Answered fields",
      render: (row) => (
        <Typography sx={{ ...body, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
          {row.answer_keys?.length ? row.answer_keys.join(", ") : "—"}
        </Typography>
      ),
    },
    {
      id: "flow_token",
      label: "Token",
      render: (row) => (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          {row.flow_token || "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          <Box
            component="button"
            type="button"
            onClick={() => onNavigate("whatsapp_forms", { tab: 1 })}
            sx={{
              ...typography.bodySmall,
              background: "none",
              border: 0,
              p: 0,
              cursor: "pointer",
              color: ColorsConstants.primaryPurple100,
            }}
          >
            View Forms
          </Box>
          {" › Unmatched replies"}
        </Typography>
        <Typography sx={typography.h5}>Unmatched replies</Typography>
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
          Replies we received but couldn't match to a form that was sent. Kept for 30 days;
          answer values are never stored here.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, "& .MuiAlert-message": typography.bodyLarge }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
        }}
      >
        {!loading && !error && count === 0 ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <Typography sx={typography.h5}>No unmatched replies</Typography>
          </Stack>
        ) : (
          <AppTable
            columns={columns}
            rows={rows}
            rowKey="id"
            loading={loading}
            maxHeight={560}
            headCellSx={{
              ...typography.bodySmall,
              backgroundColor: dashboardCardBgColor,
              color: grayFontColor,
              padding: "9px 18px",
              borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            }}
            bodyCellSx={{ padding: "10px 18px" }}
            rowSx={() => ({
              backgroundColor: "#FFFFFF",
              "& td": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderTop: "none",
              },
              "&:last-child td": { borderBottom: "none" },
            })}
            paperSx={{ border: "none", borderRadius: 0, boxShadow: "none", mb: 0 }}
            count={count}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
          />
        )}
      </Box>
    </Box>
  );
};

export default UnmatchedReplies;
